import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  isDevMode,
} from '@angular/core';
import { ServiceWorkerModule } from '@angular/service-worker';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, TitleStrategy } from '@angular/router';
import { CommonModule, registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { HttpAppErrorInterceptor } from './core/interceptors/error.interceptor';
import { TenantInterceptor } from './core/interceptors/tenant.interceptor';
import { NgswBypassInterceptor } from './core/interceptors/ngsw-bypass.interceptor';
import { ThemeService } from './shared/services/theme.service';
import { TenantService } from './core/services/tenant.service';
import { AppTitleStrategy } from './core/services/app-title.strategy';

import { ToastrModule } from 'ngx-toastr';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { GoogleMapsModule } from '@angular/google-maps';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';

import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  SOCIAL_AUTH_CONFIG,
  SocialAuthServiceConfig,
  SocialLoginModule,
} from '@abacritt/angularx-social-login';

import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MsalModule, MsalService, MSAL_INSTANCE } from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';

import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SliderModule } from 'primeng/slider';
import { MultiSelectModule } from 'primeng/multiselect';
import { ListboxModule } from 'primeng/listbox';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { PanelModule } from 'primeng/panel';
import { TabsModule } from 'primeng/tabs';
import { AccordionModule } from 'primeng/accordion';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from 'src/environments/environment';

// Sem timeout artificial de propósito: se o boot travar aqui, a tela branca é um
// sinal visível de que getTheme/loadThemeFromTenant ainda está lento — mascarar isso
// com um timeout esconderia o sintoma sem resolver a causa (contenção no backend).
export function initializeTheme(
  themeService: ThemeService,
  tenantService: TenantService
) {
  return async () => {
    const tenantId = tenantService.tenantId;

    if (!tenantId) {
      console.warn('[initializeTheme] tenantId ausente; pulando carregamento de tema.');
      return;
    }

    try {
      await firstValueFrom(
        forkJoin({
          theme: themeService.loadThemeFromTenant(tenantId).pipe(
            catchError((err) => {
              console.error('[initializeTheme] loadThemeFromTenant falhou; continuando bootstrap.', err);
              return of(null);
            }),
          ),
          tenantTheme: tenantService.getTheme().pipe(
            catchError((err) => {
              console.error('[initializeTheme] getTheme falhou; continuando bootstrap.', err);
              return of(null);
            }),
          ),
        })
      );
    } catch (err) {
      console.error('[initializeTheme] erro inesperado; continuando bootstrap.', err);
    }
  };
}

// --- FÁBRICA MSAL CORRIGIDA ---
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.microsoftClientId || '36d1ab9b-76a3-49a4-b202-22e37fda2302',
      authority: 'https://login.microsoftonline.com/common',
      // Se for rodar local, use window.location.origin + '/'
      redirectUri: window.location.origin + '/'
    }
  });
}
registerLocaleData(localePt);

const MyConnectSeaPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{primary.50}',
      100: '{primary.100}',
      200: '{primary.200}',
      300: '{primary.300}',
      400: '{primary.400}',
      500: '{primary.500}',
      600: '{primary.600}',
      700: '{primary.700}',
      800: '{primary.800}',
      900: '{primary.900}',
      950: '{primary.950}',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#0f172a',
          950: '#020617',
        },
      },
    },
  },
});

@NgModule({
  declarations: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    AppRoutingModule,
    SharedModule,
    NgMultiSelectDropDownModule.forRoot(),
    NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
    SocialLoginModule,
    MsalModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
    CoreModule,
    GoogleMapsModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    TableModule,
    DialogModule,
    SelectModule,
    DatePickerModule,
    CheckboxModule,
    RadioButtonModule,
    InputNumberModule,
    TextareaModule,
    SelectButtonModule,
    SliderModule,
    MultiSelectModule,
    ListboxModule,
    MenuModule,
    MenubarModule,
    PanelModule,
    TabsModule,
    AccordionModule,
    ToastModule,
    MonacoEditorModule.forRoot({
      baseUrl: 'assets/monaco/min/vs',
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Check for an updated ngsw manifest whenever the app has been stable
      // (idle) for 30s after bootstrap, instead of racing app startup.
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    // Título da aba por módulo e ambiente (#456).
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyConnectSeaPreset,
        options: {
          darkModeSelector: '.my-app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'base, components, primeng, utilities',
          },
        },
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpAppErrorInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NgswBypassInterceptor, multi: true },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    MsalService,
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.googleClientId, {
              oneTapEnabled: false,
            }),
          },
          {
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider('232682211332704'),
          },
        ],
        onError: (err: any) => console.error(err),
      } as SocialAuthServiceConfig,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTheme,
      deps: [ThemeService, TenantService],
      multi: true,
    },
    DialogService,
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
